import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { fetchAll } from '@/lib/paginate'
import { recurringHorizonISO } from '@/lib/recurring'
import { useHousehold } from '@/context/HouseholdContext'
import { isFxSupported } from '@/lib/currency'
import { endOfMonthISO } from '@/lib/finance'
import { suggestStatementMonth } from '@/lib/cards'
import type { AccountType, Transaction, TransactionKind, TransactionStatus } from '@/types/db'

export interface TransactionRow extends Transaction {
  account?: { name: string; currency: string; icon: string | null; type: AccountType } | null
  category?: { name: string; color: string | null } | null
}

export interface TransactionFilters {
  from?: string
  to?: string
  accountId?: string
  categoryId?: string
}

// Todas as queries que dependem de valores de lançamento — invalidadas JUNTAS após
// qualquer mutação. Inclui as PENDÊNCIAS do dashboard (pendingByAccount/pendingUntil)
// e a próxima ocorrência (nextOccurrences), que antes ficavam obsoletas até um reload
// ao marcar pago / incluir recorrente / editar. Um lugar só evita esquecer uma chave.
function invalidateMoneyQueries(qc: ReturnType<typeof useQueryClient>) {
  for (const key of [
    'transactions',
    'balances',
    'cardMonthFatura',
    'cardTotalDebt',
    'cardLandingMonth',
    'pendingByAccount',
    'pendingUntil',
    'nextOccurrences',
  ]) {
    void qc.invalidateQueries({ queryKey: [key] })
  }
}

// Data de HOJE em ISO (yyyy-mm-dd) pela DATA LOCAL. `toISOString()` é UTC: à noite
// (usuário em UTC-3/UTC-6) já virou o dia seguinte, e uma parcela/ocorrência "de hoje"
// era marcada paga um dia antes. Sempre construir por partes locais.
function todayISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Dívida por conta: soma de TODAS as despesas pendentes (qualquer data, mês
// atual + futuros), na MOEDA BASE (amount_base) — consistente mesmo com
// lançamentos em moedas diferentes no mesmo cartão. Usada no dashboard p/ cartão.
export function usePendingExpenseByAccount() {
  const { householdId } = useHousehold()
  return useQuery({
    queryKey: ['pendingByAccount', householdId],
    queryFn: async () => {
      const data = await fetchAll<{ account_id: string; amount_base: number | null }>((from, to) =>
        supabase
          .from('transactions')
          .select('account_id, amount_base')
          .eq('household_id', householdId)
          .eq('kind', 'expense')
          .eq('status', 'pending')
          .range(from, to),
      )
      const map = new Map<string, number>()
      for (const t of data) {
        if (t.amount_base == null) continue
        map.set(t.account_id, (map.get(t.account_id) ?? 0) + Number(t.amount_base))
      }
      return map
    },
  })
}

export interface TransferInput {
  fromAccountId: string
  toAccountId: string
  fromAmount: number
  fromCurrency: string
  toAmount: number
  toCurrency: string
  date: string
  description: string | null
}

// Transferência entre contas = PAR de lançamentos com o mesmo transfer_group_id:
// saída (expense) na origem + entrada (income) no destino. Move o saldo corretamente
// e é excluída dos totais de receita/despesa (filtram por transfer_group_id == null).
export function useSaveTransfer() {
  const { householdId } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (t: TransferInput) => {
      const groupId = crypto.randomUUID()
      const desc = t.description?.trim() || 'Transferência'
      const { error } = await supabase.from('transactions').insert([
        {
          household_id: householdId,
          account_id: t.fromAccountId,
          category_id: null,
          kind: 'expense',
          amount: t.fromAmount,
          currency: t.fromCurrency,
          date: t.date,
          description: desc,
          status: 'paid',
          transfer_group_id: groupId,
        },
        {
          household_id: householdId,
          account_id: t.toAccountId,
          category_id: null,
          kind: 'income',
          amount: t.toAmount,
          currency: t.toCurrency,
          date: t.date,
          description: desc,
          status: 'paid',
          transfer_group_id: groupId,
        },
      ])
      if (error) throw error
    },
    onSuccess: () => invalidateMoneyQueries(qc),
  })
}

// Edita uma transferência existente. Mantém o MESMO transfer_group_id: apaga as
// duas pernas antigas e insere o par novo (origem/destino podem ter trocado, então
// recriar é mais simples e correto que casar perna a perna).
export function useUpdateTransfer() {
  const { householdId } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (t: TransferInput & { groupId: string }) => {
      const desc = t.description?.trim() || 'Transferência'
      const { error: delErr } = await supabase
        .from('transactions')
        .delete()
        .eq('transfer_group_id', t.groupId)
        .eq('household_id', householdId)
      if (delErr) throw delErr
      const { error } = await supabase.from('transactions').insert([
        {
          household_id: householdId,
          account_id: t.fromAccountId,
          category_id: null,
          kind: 'expense',
          amount: t.fromAmount,
          currency: t.fromCurrency,
          date: t.date,
          description: desc,
          status: 'paid',
          transfer_group_id: t.groupId,
        },
        {
          household_id: householdId,
          account_id: t.toAccountId,
          category_id: null,
          kind: 'income',
          amount: t.toAmount,
          currency: t.toCurrency,
          date: t.date,
          description: desc,
          status: 'paid',
          transfer_group_id: t.groupId,
        },
      ])
      if (error) throw error
    },
    onSuccess: () => invalidateMoneyQueries(qc),
  })
}

// Despesas PENDENTES (não pagas) com data até `untilISO`, por conta — inclui
// atrasadas de meses anteriores. Usado no dashboard p/ "dívidas em aberto".
export function usePendingExpensesUntil(untilISO: string) {
  const { householdId } = useHousehold()
  return useQuery({
    queryKey: ['pendingUntil', householdId, untilISO],
    queryFn: async () => {
      const data = await fetchAll<{ account_id: string; amount_base: number | null }>((from, to) =>
        supabase
          .from('transactions')
          .select('account_id, amount_base')
          .eq('household_id', householdId)
          .eq('kind', 'expense')
          .eq('status', 'pending')
          .lte('date', untilISO)
          .range(from, to),
      )
      return data
    },
  })
}

export function useTransactions(filters: TransactionFilters = {}) {
  const { householdId } = useHousehold()
  return useQuery({
    queryKey: ['transactions', householdId, filters],
    queryFn: async () => {
      // Pagina para não truncar em 1000 (a lista/somatórios ficariam incompletos).
      const data = await fetchAll<TransactionRow>((from, to) => {
        let q = supabase
          .from('transactions')
          .select('*, account:accounts(name, currency, icon, type), category:categories(name, color)')
          .eq('household_id', householdId)
          .order('date', { ascending: false })
          .order('created_at', { ascending: false })
        if (filters.from) q = q.gte('date', filters.from)
        if (filters.to) q = q.lte('date', filters.to)
        if (filters.accountId) q = q.eq('account_id', filters.accountId)
        if (filters.categoryId) q = q.eq('category_id', filters.categoryId)
        return q.range(from, to)
      })
      return data
    },
  })
}

// Compras da FATURA de um mês (régua statement_month), como LINHAS completas — para
// a lista de Transações mostrar cada compra de cartão no mês da FATURA a que pertence,
// não no mês da DATA da compra. Uma compra feita após o fechamento cai na fatura (mês)
// seguinte; sem isto, ao filtrar, ela apareceria no mês em que foi feita junto com a
// fatura já paga deste mês. Só COMPRAS/estornos (exclui transferências/pagamentos, que
// seguem pela data). Espelha o recorte de faturaMonthOf: statement_month no mês, ou
// (sem statement_month) data no mês.
export function useCardMonthStatementTxs(year: number, month0: number) {
  const { householdId } = useHousehold()
  const mk = `${year}-${String(month0 + 1).padStart(2, '0')}`
  const first = `${mk}-01`
  const last = endOfMonthISO(year, month0)
  return useQuery({
    queryKey: ['cardMonthStatementTxs', householdId, mk],
    queryFn: async (): Promise<TransactionRow[]> => {
      const { data: accts, error: aerr } = await supabase
        .from('accounts')
        .select('id')
        .eq('household_id', householdId)
        .eq('type', 'cartao')
      if (aerr) throw aerr
      const cardIds = (accts ?? []).map((a) => a.id)
      if (cardIds.length === 0) return []
      return fetchAll<TransactionRow>((from, to) =>
        supabase
          .from('transactions')
          .select('*, account:accounts(name, currency, icon, type), category:categories(name, color)')
          .eq('household_id', householdId)
          .in('account_id', cardIds)
          .is('transfer_group_id', null)
          .or(
            `and(statement_month.gte.${first},statement_month.lte.${last}),and(statement_month.is.null,date.gte.${first},date.lte.${last})`,
          )
          .order('date', { ascending: false })
          .order('created_at', { ascending: false })
          .range(from, to),
      )
    },
  })
}

export interface TransactionInput {
  account_id: string
  category_id: string | null
  kind: TransactionKind
  amount: number
  currency: string
  date: string
  description: string | null
  status: TransactionStatus
  exchange_rate?: number | null // override manual opcional (taxa -> base GTQ)
  amount_account?: number | null // valor na moeda da conta (moeda ≠ da conta)
  tags?: string[] // nomes das tags (coluna text[])
  statement_month?: string | null // cartão: mês da fatura (dia 01). null = mês da data
}

// Após gravar, se amount_base ficou null (taxa ausente no cache), busca a taxa
// da DATA da transação (histórica) via Edge Function e reconverte. Usa mode
// 'pairs' com o par+data específicos — assim uma transação RETROATIVA pega a
// taxa do dia dela (não a de hoje).
async function ensureConversion(currency: string, base: string, date: string) {
  try {
    await supabase.functions.invoke('sync-rates', {
      body: { mode: 'pairs', pairs: [{ from: currency, to: base, date }] },
    })
  } catch (e) {
    // Falha de rede/Edge Function não bloqueia o CRUD: amount_base fica pendente.
    console.warn('[sync-rates] backfill falhou:', e)
  }
}

// Pagar a fatura LIQUIDA as compras COBRADAS nela: marca como 'paid' as pendentes
// do cartão cuja FATURA é <= o mês pago. Uma compra empurrada para uma fatura
// futura (feita depois do fechamento) NÃO é liquidada aqui — ela ainda não foi
// cobrada. Parcelas de meses futuros idem.
//
// Isso é só semântica/exibição: o status de uma compra de cartão não entra em
// nenhum cálculo (a dívida do cartão soma todos os status, porque compra lançada
// já é gasto real). Mas deixar "Pendente" numa fatura paga é mentir para o leitor.
export function useSettleCardMonth() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ cardId, year, month0 }: { cardId: string; year: number; month0: number }) => {
      const monthISO = `${year}-${String(month0 + 1).padStart(2, '0')}-01`
      const endISO = endOfMonthISO(year, month0)
      const { data, error } = await supabase
        .from('transactions')
        .update({ status: 'paid' })
        .eq('account_id', cardId)
        .eq('status', 'pending')
        .is('transfer_group_id', null) // o pagamento em si já nasce 'paid'
        // sem fatura marcada → vale a data; com fatura marcada → vale a fatura
        .or(`and(statement_month.is.null,date.lte.${endISO}),statement_month.lte.${monthISO}`)
        .select('id')
      if (error) throw error
      return data?.length ?? 0
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['transactions'] })
      void qc.invalidateQueries({ queryKey: ['balances'] })
      void qc.invalidateQueries({ queryKey: ['cardMonthFatura'] })
    },
  })
}

export function useSaveTransaction() {
  const { householdId, baseCurrency } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: TransactionInput }) => {
      const payload = { ...values, household_id: householdId }
      let saved: Transaction | null = null
      if (id) {
        const { data, error } = await supabase.from('transactions').update(payload).eq('id', id).select().single()
        if (error) throw error
        saved = data as Transaction
      } else {
        const { data, error } = await supabase.from('transactions').insert(payload).select().single()
        if (error) throw error
        saved = data as Transaction
      }
      // taxa ausente + moeda suportada => backfill da taxa da DATA da transação
      if (saved && saved.amount_base === null && isFxSupported(saved.currency)) {
        await ensureConversion(saved.currency, baseCurrency, saved.date)
        // relê a transação já convertida
        const { data: fresh } = await supabase.from('transactions').select('*').eq('id', saved.id).single()
        if (fresh) saved = fresh as Transaction
      }
      return saved
    },
    onSuccess: () => invalidateMoneyQueries(qc),
  })
}

// 'YYYY-MM' avançado em `k` meses (vira o ano corretamente).
function addMonthsYM(ym: string, k: number): string {
  const [y, m] = ym.split('-').map(Number)
  const d = new Date(y, m - 1 + k, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export interface InstallmentsInput {
  base: TransactionInput // amount = valor DE CADA parcela; date = data da 1ª
  count: number // número de parcelas (>= 2)
  firstStatementMonth: string | null // 'YYYY-MM' da fatura da 1ª parcela; null = não-cartão
  // Valor EXATO de cada parcela (na moeda da transação e, se cross-currency, na
  // moeda da conta). Somam ao total EXATO — o centavo do resto de uma divisão
  // (ex.: 1000/3) fica numa parcela, não some. Se omitido, cai em base.amount.
  amounts?: number[]
  amountsAccount?: (number | null)[]
}

// COMPRA PARCELADA (cartão): uma compra única dividida em N. Cria N transações
// com installment_no/total (o badge mostra "1/10"), datas em progressão mensal e
// faturas CONSECUTIVAS a partir da primeira. Não é recorrente — é um gasto só,
// fatiado. Parcela com data <= hoje entra paga; futura, pendente.
export function useSaveInstallments() {
  const { householdId, baseCurrency } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ base, count, firstStatementMonth, amounts, amountsAccount }: InstallmentsInput) => {
      const today = todayISO()
      const rows = Array.from({ length: count }, (_, k) => {
        const date = occurrenceDate(base.date, 'monthly', 1, k)
        return {
          household_id: householdId,
          account_id: base.account_id,
          category_id: base.category_id || null,
          kind: base.kind,
          amount: amounts?.[k] ?? base.amount,
          currency: base.currency,
          date,
          description: base.description,
          status: (date <= today ? base.status : 'pending') as TransactionStatus,
          exchange_rate: base.exchange_rate ?? null,
          amount_account: amountsAccount?.[k] ?? base.amount_account ?? null,
          tags: base.tags ?? [],
          installment_no: k + 1,
          installment_total: count,
          statement_month: firstStatementMonth ? `${addMonthsYM(firstStatementMonth, k)}-01` : null,
        }
      })
      const { data, error } = await supabase.from('transactions').insert(rows).select('id, amount_base, currency, date')
      if (error) throw error
      // Backfill de taxa para as parcelas que ficaram sem amount_base (moeda ≠ base
      // sem taxa em cache na data): busca a taxa da data E reconverte a linha (buscar
      // a taxa não recalcula sozinho as linhas já inseridas).
      const pend = ((data ?? []) as { id: string; amount_base: number | null; currency: string; date: string }[])
        .filter((t) => t.amount_base === null && isFxSupported(t.currency))
      for (const t of pend) {
        await ensureConversion(t.currency, baseCurrency, t.date)
        const { error: rcErr } = await supabase.rpc('fx_reconvert_transaction', { p_id: t.id })
        if (rcErr) console.warn('[fx_reconvert] falhou:', rcErr)
      }
      return count
    },
    onSuccess: () => invalidateMoneyQueries(qc),
  })
}

export interface ImportRow {
  account_id: string
  category_id: string | null
  kind: TransactionKind
  amount: number
  currency: string
  date: string
  description: string | null
  status: TransactionStatus
  statement_month: string | null
  installment_no?: number | null
  installment_total?: number | null
}

// Insere N lançamentos de uma vez (import de fatura de cartão). O trigger calcula
// amount_base; para linhas que ficarem sem base (moeda ≠ base sem taxa em cache na
// data) busca a taxa histórica da data e o backfill reconverte.
export function useImportStatement() {
  const { householdId, baseCurrency } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (rows: ImportRow[]) => {
      if (rows.length === 0) return 0
      const payload = rows.map((r) => ({
        household_id: householdId,
        account_id: r.account_id,
        category_id: r.category_id || null,
        kind: r.kind,
        amount: r.amount,
        currency: r.currency,
        date: r.date,
        description: r.description,
        status: r.status,
        statement_month: r.statement_month,
        installment_no: r.installment_no ?? null,
        installment_total: r.installment_total ?? null,
      }))
      const { data, error } = await supabase
        .from('transactions')
        .insert(payload)
        .select('id, amount_base, currency, date')
      if (error) throw error
      // Linhas que ficaram sem base (moeda ≠ base e taxa da data ainda não estava
      // no cache). O trigger de INSERT não tinha a taxa, então gravou amount_base null.
      const pend = ((data ?? []) as { id: string; amount_base: number | null; currency: string; date: string }[])
        .filter((t) => t.amount_base === null && isFxSupported(t.currency))
      // 1) Busca a taxa histórica da data (uma vez por par+data).
      const uniq = new Map<string, { currency: string; date: string }>()
      for (const t of pend) uniq.set(`${t.currency}|${t.date}`, t)
      for (const p of uniq.values()) await ensureConversion(p.currency, baseCurrency, p.date)
      // 2) Reconverte cada linha pendente agora que a taxa existe. Sem isso, buscar a
      // taxa NÃO recalcula sozinho as linhas já inseridas — elas ficariam null e fora
      // dos somatórios (dívida do cartão, orçamento) até serem reeditadas na mão.
      // fx_reconvert_transaction é idempotente: só age se ainda null e houver taxa.
      for (const t of pend) {
        const { error: rcErr } = await supabase.rpc('fx_reconvert_transaction', { p_id: t.id })
        if (rcErr) console.warn('[fx_reconvert] falhou:', rcErr)
      }
      return payload.length
    },
    onSuccess: () => invalidateMoneyQueries(qc),
  })
}

// Exclui uma transação. Se for perna de uma TRANSFERÊNCIA (transfer_group_id
// preenchido), apaga o GRUPO inteiro — as duas pernas — senão o saldo da outra
// conta ficaria com uma entrada/saída órfã. Aceita o id direto (retrocompat) ou
// { id, transferGroupId }.
export function useDeleteTransaction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (arg: string | { id: string; transferGroupId?: string | null }) => {
      const id = typeof arg === 'string' ? arg : arg.id
      const transferGroupId = typeof arg === 'string' ? null : (arg.transferGroupId ?? null)
      const q = supabase.from('transactions').delete()
      const { error } = transferGroupId
        ? await q.eq('transfer_group_id', transferGroupId)
        : await q.eq('id', id)
      if (error) throw error
    },
    onSuccess: () => invalidateMoneyQueries(qc),
  })
}

// Alterna o status pendente <-> pago (checkbox na lista). Não recalcula
// amount_base (o trigger só reage a amount/currency/date).
export function useToggleStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: TransactionStatus }) => {
      const { error } = await supabase.from('transactions').update({ status }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => invalidateMoneyQueries(qc),
  })
}

// ------------------------------- Recorrentes --------------------------------
export type RecurrenceFrequency = 'weekly' | 'monthly' | 'yearly'

export interface RecurringInput {
  base: TransactionInput // 1ª ocorrência / template
  frequency: RecurrenceFrequency
  interval: number
  maxOccurrences: number | null // N parcelas, ou null = sem fim
  endDate?: string | null // data-fim solta (independente de parcelado) — null = sem fim
  daysOfWeek?: number[] | null // semanal em dias escolhidos (0=Dom..6=Sáb)
}

// A k-ésima data (0-based) a partir de `startISO`, no passo da frequência.
export function occurrenceDate(startISO: string, frequency: RecurrenceFrequency, interval: number, k: number): string {
  const [y, m, d] = startISO.split('-').map(Number)
  const step = Math.max(1, interval) * k
  let dt: Date
  if (frequency === 'weekly') dt = new Date(y, m - 1, d + step * 7)
  else if (frequency === 'yearly') dt = new Date(y + step, m - 1, d)
  else {
    // mensal: mantém o dia, encurtando em meses curtos (31 → 30/28)
    const tgt = new Date(y, m - 1 + step, 1)
    const last = new Date(tgt.getFullYear(), tgt.getMonth() + 1, 0).getDate()
    dt = new Date(tgt.getFullYear(), tgt.getMonth(), Math.min(d, last))
  }
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
}

// Cria a regra. PARCELADO (maxOccurrences=N) materializa as N parcelas na hora —
// um parcelamento é um conjunto FIXO e conhecido, o usuário espera ver as 10 já.
// SEM FIM (null) não gera nada: as ocorrências entram sob demanda pelo botão
// "Incluir recorrente" (uma assinatura é aberta, não faz sentido materializar tudo).
export function useSaveRecurring() {
  const { householdId } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ base, frequency, interval, maxOccurrences, endDate, daysOfWeek }: RecurringInput) => {
      // Só faz sentido no semanal; vazio vira NULL (comportamento clássico).
      const dow = frequency === 'weekly' && daysOfWeek && daysOfWeek.length > 0 ? daysOfWeek : null
      const template = {
        account_id: base.account_id,
        category_id: base.category_id,
        kind: base.kind,
        amount: base.amount,
        currency: base.currency,
        description: base.description,
        status: base.status,
        tags: [] as string[],
      }
      const { data: rule, error } = await supabase
        .from('recurring_rules')
        .insert({
          household_id: householdId,
          account_id: base.account_id,
          frequency,
          interval,
          start_date: base.date,
          next_date: base.date,
          max_occurrences: maxOccurrences,
          end_date: endDate ?? null,
          days_of_week: dow,
          template,
        })
        .select()
        .single()
      if (error) throw error

      if (maxOccurrences && maxOccurrences > 0) {
        // Cartão: cada parcela cai numa fatura consecutiva. A data da parcela é a
        // data da COMPRA (progressão mensal), então usa a régra de fechamento —
        // parcela 1 pode já cair na fatura seguinte (BAC: compra 14/07 → agosto).
        const { data: acc } = await supabase
          .from('accounts')
          .select('type, due_day, closing_day')
          .eq('id', base.account_id)
          .single()
        const card = acc?.type === 'cartao' ? (acc as { due_day: number | null; closing_day: number | null }) : null
        const today = todayISO()

        const rows = Array.from({ length: maxOccurrences }, (_, k) => {
          const date = occurrenceDate(base.date, frequency, interval, k)
          return {
            household_id: householdId,
            account_id: base.account_id,
            category_id: base.category_id || null,
            kind: base.kind,
            amount: base.amount,
            currency: base.currency,
            date,
            description: base.description,
            // 1ª parcela já feita entra paga; as futuras, pendentes.
            status: (date <= today ? 'paid' : 'pending') as TransactionStatus,
            recurring_id: rule.id,
            exchange_rate: base.exchange_rate ?? null,
            amount_account: base.amount_account ?? null,
            statement_month: card ? suggestStatementMonth(date, card.closing_day, card.due_day) : null,
          }
        })
        const { error: occErr } = await supabase.from('transactions').insert(rows)
        if (occErr) throw occErr
      }
      return rule
    },
    onSuccess: () => invalidateMoneyQueries(qc),
  })
}

// Edita uma ocorrência recorrente com escopo:
//  - 'one':    só esta ocorrência (update simples).
//  - 'future': esta e as próximas JÁ LANÇADAS (mesmo recurring_id, date >= data
//              desta, ainda pendentes). NÃO mexe na regra — editar a REGRA (e
//              valer para as futuras ainda não lançadas) é coisa da tela
//              Recorrentes (useUpdateRecurringRule), não do formulário de
//              transação. Editar uma transação nunca deve alterar o recorrente.
// Propaga: conta, categoria, valor, moeda, descrição, TAGS. NÃO propaga a data
// nem o mês da fatura (statement_month) — cada ocorrência tem a sua data e,
// portanto, cai na fatura dela. O trigger reconverte amount_base por linha.
//
// ATENÇÃO ao mexer aqui: os updates listam os campos UM A UM. Campo que não
// estiver na lista é silenciosamente descartado — o banco não reclama e a UI
// mostra "salvo com sucesso". Foi o que acontecia com `tags` e `amount_account`.
export type EditScope = 'one' | 'future'

export function useEditRecurring() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      transaction,
      values,
      scope,
    }: {
      transaction: Transaction
      values: TransactionInput
      scope: EditScope
    }) => {
      if (scope === 'one' || !transaction.recurring_id) {
        const { error } = await supabase
          .from('transactions')
          .update({
            account_id: values.account_id,
            category_id: values.category_id,
            kind: values.kind,
            amount: values.amount,
            currency: values.currency,
            date: values.date,
            description: values.description,
            status: values.status,
            exchange_rate: values.exchange_rate ?? null,
            tags: values.tags ?? [],
            amount_account: values.amount_account ?? null,
            statement_month: values.statement_month ?? null,
          })
          .eq('id', transaction.id)
        if (error) throw error
        return
      }

      // scope 'future': esta e as próximas (por data), sem alterar as datas.
      const propagate = {
        account_id: values.account_id,
        category_id: values.category_id,
        kind: values.kind,
        amount: values.amount,
        currency: values.currency,
        description: values.description,
        tags: values.tags ?? [],
      }
      const { error: upErr } = await supabase
        .from('transactions')
        .update(propagate)
        .eq('recurring_id', transaction.recurring_id)
        .gte('date', transaction.date)
      if (upErr) throw upErr
      // (A regra em si NÃO é tocada aqui — só a tela de Recorrentes edita a
      // regra. Ver comentário da função.)
    },
    onSuccess: () => invalidateMoneyQueries(qc),
  })
}

// Gera ocorrências recorrentes devidas até hoje/fim do mês (chamar ao abrir o app).
export function useGenerateDueRecurring() {
  const { householdId } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('recurring_generate_due', {
        p_household_id: householdId,
        p_until: recurringHorizonISO(),
      })
      if (error) throw error
      return data as number
    },
    onSuccess: (count) => {
      if (count && count > 0) invalidateMoneyQueries(qc)
    },
  })
}
